/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { PageHeader, Table, TableRow, TableColumn, Pager, SearchBar, DropdownSelect, Chip, KebabMenu, Anchor, Modal, Button, AddTwoToneIcon } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import roleSkillManagementStyles from '../role-skill-management.module.css';
import classNames from 'classnames';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { SortBy } from '../../../components/general/sort-by/sort-by';
import { SORT_BY_OPTIONS } from '../../../library/consts/consts';
import { SortByOption } from '../../../enums/sort-by-option';
import styles from './manage-skill-categories.module.css';
import { SearchSkillCategoryListRequest, SearchSkillCategoryListResponse, SearchSkillCategoryListItemResponse } from '../../../overmind/actions';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { FetchType } from '../../../enums';
import { ConfirmationModalLayout } from '../../../widgets/layouts';

enum Status {
  Active = 'Active',
  Archived = 'Archived',
}

export function ManageSkillCategoriesPage(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [request, setRequest] = useState<SearchSkillCategoryListRequest>({ sortBy: SortByOption.AlphabeticalAtoZ, page: 1 });
  const [data, setData] = useState<SearchSkillCategoryListResponse>();
  const [skillCategoryToArchive, setSkillCategoryToArchive] = useState<SearchSkillCategoryListItemResponse>();
  const [skillCategoryToUnarchive, setSkillCategoryToUnarchive] = useState<SearchSkillCategoryListItemResponse>();
  const [skillCategoryToDelete, setSkillCategoryToDelete] = useState<SearchSkillCategoryListItemResponse>();

  async function getData(request: SearchSkillCategoryListRequest) {
    const response = await actions.searchSkillCategories(request);
    setData(response);
  }

  useEffect(() => {
    async function initPage() {
      setIsLoading(true);
      await getData(request);
      setIsLoading(false);
    }

    initPage();
  }, [companyVariables.slug]);

  function viewSkillCategory(skillCategorySlug: string) {
    actions.startLoader({ path: PagePath.roleSkillManagementSkillCategoriesEdit, type: FetchType.PageFetching });
    keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementSkillCategoriesView.replace(':companySlug', companyVariables.slug!).replace(':skillCategorySlug', skillCategorySlug)}`);
  }

  return (
    <React.Fragment>
      <div className="wrapper stack" style={{ gap: 24 }}>
        <PageHeader
          breadcrumbs={[
            { name: 'Roles & skills', url: PagePath.roleSkillManagementBase },
            { name: 'Manage skill categories', url: location.pathname },
          ]}
          title="Manage skill categories"
          subtitle="A library of all the skills categories within your company"
        />

        <div className={roleSkillManagementStyles.actions} style={{ justifyContent: 'flex-start' }}>
          <div className={styles.action} style={{ width: 300 }}>
            <SearchBar
              loading={isSearching}
              label="Search"
              responsive
              onInput={async event => {
                setIsSearching(true);
                const search = (event.target as HTMLInputElement).value;
                const nextRequest = { ...request, search, page: 1 };
                setRequest(nextRequest);
                await getData(nextRequest);
                setIsSearching(false);
              }}
            />
          </div>

          <div className={styles.action}>
            <DropdownSelect
              label="Status"
              options={[{ value: Status.Active }, { value: Status.Archived }]}
              value={request.isArchived ? Status.Archived : Status.Active}
              onChange={async event => {
                setIsLoading(true);
                const value = (event.target as HTMLSelectElement).value;
                const nextRequest: SearchSkillCategoryListRequest = { ...request, isArchived: value === Status.Archived };
                setRequest(nextRequest);
                await getData(nextRequest);
                setIsLoading(false);
              }}
              responsive
            />
          </div>

          <SortBy
            options={SORT_BY_OPTIONS.filter(option => option.value !== SortByOption.MostRecentAssigned && option.value !== SortByOption.RecentlyCompleted && option.value !== SortByOption.ScoreHighToLow && option.value !== SortByOption.ScoreLowToHigh)}
            value={request.sortBy}
            onChange={async event => {
              setIsLoading(true);
              const sortBy = (event.target as HTMLSelectElement).value;
              const nextRequest: SearchSkillCategoryListRequest = { ...request, sortBy: sortBy as SortByOption };
              setRequest(nextRequest);
              await getData(nextRequest);
              setIsLoading(false);
            }}
          />

          <div className={styles.button}>
            <Button type="button" square theme="dark" onClick={() => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementSkillCategoriesCreate.replace(':companySlug', companyVariables.slug!)}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AddTwoToneIcon />
                Create skill category
              </div>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <SkeletonLoader height="500px" />
        ) : !data?.results.length ? (
          <EmptyState />
        ) : (
          // Avoids incorrect spacing in mobile view
          <div style={{ width: '100%' }}>
            <Table
              standAloneConfigColumn
              footerContent={
                <div className={classNames({ invisible: data.totalPages <= 1 })}>
                  <PagerConnector
                    defaultPageNumber={request.page}
                    pageCount={data?.totalPages ?? 0}
                    onPageChange={async page => {
                      if (page === request.page) return;
                      setIsLoading(true);
                      const nextRequest = { ...request, page };
                      setRequest(nextRequest);
                      await getData(nextRequest);
                      setIsLoading(false);
                    }}
                  >
                    {connector => <Pager {...connector} isLoading={isLoading} />}
                  </PagerConnector>
                </div>
              }
            >
              {data?.results.map(skillCategory => (
                <TableRow
                  key={skillCategory.slug}
                  id={skillCategory.slug!}
                  configCell={() => (
                    <KebabMenu
                      items={[
                        {
                          label: 'View',
                          onClick: () => viewSkillCategory(skillCategory.slug!),
                        },
                        {
                          label: 'Edit',
                          onClick: () => {
                            actions.startLoader({ path: PagePath.roleSkillManagementSkillCategoriesEdit, type: FetchType.PageFetching });
                            keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementSkillCategoriesEdit.replace(':companySlug', companyVariables.slug!).replace(':skillCategorySlug', skillCategory.slug!)}`);
                          },
                        },
                        {
                          label: skillCategory.isArchived ? 'Unarchive' : 'Archive',
                          onClick: () => (skillCategory.isArchived ? setSkillCategoryToUnarchive(skillCategory) : setSkillCategoryToArchive(skillCategory)),
                        },
                        {
                          label: 'Delete',
                          onClick: () => setSkillCategoryToDelete(skillCategory),
                          disabled: !skillCategory.isArchived,
                        },
                      ]}
                    />
                  )}
                >
                  <TableColumn id="skill" label="Skill">
                    <Anchor hovertype="opacity" textTransform="none" onClick={() => viewSkillCategory(skillCategory.slug!)}>
                      {skillCategory.name}
                    </Anchor>
                  </TableColumn>

                  <TableColumn id="description" label="Description" emptyText="No description provided">
                    <div className={roleSkillManagementStyles.description}>{skillCategory.description || 'No description provided'}</div>
                  </TableColumn>

                  <TableColumn id="skills" label="Skills">
                    {skillCategory.skillCount}
                  </TableColumn>

                  <TableColumn id="status" label="Status">
                    {skillCategory.isArchived ? <Chip>Archived</Chip> : <Chip themeColour="g">Active</Chip>}
                  </TableColumn>
                </TableRow>
              ))}
            </Table>
          </div>
        )}
      </div>

      <Modal open={!!skillCategoryToArchive} onClose={() => setSkillCategoryToArchive(undefined)}>
        {!!skillCategoryToArchive && (
          <ConfirmationModalLayout
            title={`Are you sure you want to archive '${skillCategoryToArchive.name}'?`}
            subtitle="The skill category will still be available in analytics, but you will not be able to use it on the platform going forward."
            cancelButtonText="No, cancel"
            onClickCancel={() => setSkillCategoryToArchive(undefined)}
            submitButtonText="Yes, archive"
            onClickSubmit={async () => {
              setIsLoading(true);
              setSkillCategoryToArchive(undefined);
              await actions.archiveSkillCategory(skillCategoryToArchive.slug);
              await getData(request);
              setIsLoading(false);
            }}
          />
        )}
      </Modal>

      <Modal open={!!skillCategoryToUnarchive} onClose={() => setSkillCategoryToUnarchive(undefined)}>
        {!!skillCategoryToUnarchive && (
          <ConfirmationModalLayout
            title={`Are you sure you want to unarchive '${skillCategoryToUnarchive?.name}'?`}
            cancelButtonText="No, cancel"
            onClickCancel={() => setSkillCategoryToUnarchive(undefined)}
            submitButtonText="Yes, unarchive"
            onClickSubmit={async () => {
              setIsLoading(true);
              setSkillCategoryToUnarchive(undefined);
              await actions.unarchiveSkillCategory(skillCategoryToUnarchive.slug);
              await getData(request);
              setIsLoading(false);
            }}
          />
        )}
      </Modal>

      <Modal open={!!skillCategoryToDelete} onClose={() => setSkillCategoryToDelete(undefined)}>
        {!!skillCategoryToDelete && (
          <ConfirmationModalLayout
            title={`Are you sure you want to delete '${skillCategoryToDelete?.name}'?`}
            cancelButtonText="No, cancel"
            onClickCancel={() => setSkillCategoryToDelete(undefined)}
            submitButtonText="Yes, delete"
            onClickSubmit={async () => {
              setIsLoading(true);
              setSkillCategoryToDelete(undefined);
              await actions.deleteSkillCategory(skillCategoryToDelete.slug);
              const nextRequest: SearchSkillCategoryListRequest = { ...request, page: 1 };
              setRequest(nextRequest);
              await getData(nextRequest);
              setIsLoading(false);
            }}
          />
        )}
      </Modal>
    </React.Fragment>
  );
}
